import React, { useState } from 'react'
import PropTypes from 'prop-types'
import phoneUtil from 'google-libphonenumber'
import { connect } from 'react-redux'
import { midas } from '@resurs/midas'
import { Accordion, Button, TextInput } from '@resurs/midas-react'
import { getText } from '../../../../../internal/TextLabels'
import { sendBasisConfirmation } from '../../../../../store/actions/basis-actions'
import { CustomerHelper } from '../../../../../util/customerHelper'
import { getDefaultForCountry } from '../../../../../models/languages'
import { CustomerTypeModel as CustomerTypes } from '../../../models/private-models'
import PhoneNumberModal from './PhoneNumberModal'
import { getDeep } from '../../../../../util/helpers'
import { getCardOrAccountNumber } from '../utils'
import { getApplication } from '../../../../../store/actions/pay-apply-actions'

const splitNames = fullName => {
  const [, firstName = fullName || '', lastName = ''] =
    /^(.*) (.*?)$/g.exec(fullName || '') || []
  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
  }
}

const getNames = props => {
  if (props.card && props.card.cardHolderName) {
    return splitNames(props.card.cardHolderName)
  }
  const customerHelper = props.addressInfo
    ? new CustomerHelper(props.addressInfo)
    : null

  if (customerHelper && !customerHelper.isAnonymous) {
    return {
      firstName:
        (customerHelper.address && customerHelper.address.firstName) ||
        (props.application && props.application.firstName),
      lastName:
        (customerHelper.address && customerHelper.address.lastName) ||
        (props.application && props.application.lastName),
    }
  }

  if (props.application && props.application.applicationResult) {
    return splitNames(
      (props.application.applicationResult.customer || {}).contactPerson,
    )
  }
  return {
    firstName: '',
    lastName: '',
  }
}

const isAccountProtected = accountNumber =>
  accountNumber.toUpperCase().includes('X')

const BasisConfirmationForm = props => {
  const getPhoneNumber = () => {
    return new CustomerHelper(props.addressInfo).mobilePhoneNumber || ''
  }

  const [phoneNumber, setPhoneNumber] = useState(getPhoneNumber())
  const [showPhoneNumberModal, setPhoneNumberModal] = useState(false)
  const [governmentId, setGovernmentId] = useState(
    props.card
      ? props.card.cardHolderGovernmentId ||
          props.card.applicant ||
          props.governmentId ||
          ''
      : props.governmentId || '',
  )
  const [nameInput, setNameInput] = useState(() => {
    const names = getNames(props)
    return names.firstName && names.lastName
      ? `${names.firstName} ${names.lastName}`
      : null
  })

  const _getCardOrAccountNumber = () => getCardOrAccountNumber(props.card)

  const customerAndAddressExists = () => {
    return (
      props.application &&
      props.application.applicationResult &&
      props.application.applicationResult.customer &&
      props.application.applicationResult.customer.registeredAddresses &&
      props.application.applicationResult.customer.registeredAddresses.length >
        0 &&
      props.application.applicationResult.customer.registeredAddresses[0].name
    )
  }

  const nameFromCustomerAddress = () => {
    if (customerAndAddressExists()) {
      return getDeep(
        props.application.applicationResult.customer.registeredAddresses[0]
          .name,
      )
    }
    return {}
  }

  const _sendBasisConfirmation = (firstname, lastname) =>
    props.sendBasisConfirmation({
      cellphone: phoneNumber,
      country: props.country,
      firstname,
      lastname,
      verificationNumber: _getCardOrAccountNumber(),
      governmentId,
      customerType: props.customerType,
    })

  const nameFromApplication = application =>
    getDeep(application.customer.registeredAddresses[0].name)

  const onSend = () => {
    const { firstName, lastName } = splitNames(nameInput)
    const name = nameFromCustomerAddress()
    if ((firstName && lastName) || (name.first && name.last)) {
      _sendBasisConfirmation(
        firstName || name.first || '\u00A0',
        lastName || name.last || '\u00A0',
      )
    } else {
      props.getApplication(props.applicationId).then(application => {
        const { first, last } = nameFromApplication(application)
        _sendBasisConfirmation(first, last)
      })
    }
  }

  const isValidPhoneNumber = () => {
    const util = phoneUtil.PhoneNumberUtil.getInstance()
    if (phoneNumber.length > 5 && phoneNumber.length < 18) {
      const _phoneNumber = util.parse(phoneNumber, props.country)
      return util.isValidNumberForRegion(
        _phoneNumber,
        util.getRegionCodeForNumber(_phoneNumber),
      )
    }
    return false
  }

  const formatAccountNumber = () => {
    return _getCardOrAccountNumber()
      .match(/.{1,4}/g)
      .join(' ')
  }

  const buttonDisabled = () => {
    return (
      isAccountProtected(_getCardOrAccountNumber()) ||
      !(nameInput && governmentId)
    )
  }

  const handlePhoneNumberChange = e => {
    setPhoneNumber(e.target.value.replace(/[A-Za-z-]+/, ''))
  }

  const printBasisConfirmation = fullName =>
    props.printBasisConfirmation({
      language: getDefaultForCountry(props.country),
      name: nameInput || fullName,
      governmentId,
      cardOrAccountNumber: formatAccountNumber(),
    })

  const handlePrint = e => {
    e.preventDefault()
    const fullName = nameFromCustomerAddress().full
    if (nameInput || fullName) {
      printBasisConfirmation(fullName)
    } else {
      props
        .getApplication(props.applicationId)
        .then(application =>
          printBasisConfirmation(nameFromApplication(application).full),
        )
    }
  }

  const sendSmsButtonDisabled = () => {
    return (
      !isValidPhoneNumber() ||
      isAccountProtected(_getCardOrAccountNumber()) ||
      !nameInput
    )
  }

  const extendedBasisConfirmationForm = () => {
    return (
      <form style={{ marginLeft: '-20px' }}>
        <div
          style={{
            width: '50%',
            display: 'inline-block',
            verticalAlign: 'bottom',
          }}
        >
          <TextInput
            id="nameInput"
            label={getText('payApply.basisConfirmation.name')}
            value={nameInput || ''}
            onChange={e => setNameInput(e.target.value)}
            required={getText('payApply.basisConfirmation.required')}
          />
          <TextInput
            id="governmentIdInput"
            label={getText('payApply.basisConfirmation.governmentId')}
            value={governmentId || ''}
            onChange={e => setGovernmentId(e.target.value)}
            required={getText('payApply.basisConfirmation.required')}
          />
        </div>
        <div
          style={{
            display: 'inline-flex',
            width: '50%',
            justifyContent: 'flex-end',
          }}
        >
          <div style={{ marginRight: '35px', verticalAlign: 'bottom' }}>
            <Button
              onClick={e => handlePrint(e)}
              text={getText('payApply.basisConfirmation.print')}
              disabled={buttonDisabled()}
            />
          </div>
          <div>
            <Button
              text={getText('payApply.basisConfirmation.sendSms')}
              onClick={() => setPhoneNumberModal(true)}
              disabled={buttonDisabled()}
            />
          </div>
        </div>
      </form>
    )
  }

  if (
    props.customerType !== CustomerTypes.NATURAL &&
    (!props.card.cardHolderName || !props.governmentId)
  ) {
    return (
      <div style={{ ...props.style }}>
        <PhoneNumberModal
          show={showPhoneNumberModal}
          onClose={() => setPhoneNumberModal(false)}
          onSend={() => {
            setPhoneNumberModal(false)
            onSend()
          }}
          sendButtonDisabled={sendSmsButtonDisabled()}
          phoneNumber={phoneNumber}
          onChange={e => handlePhoneNumberChange(e)}
        />
        <Accordion
          values={[
            {
              key: 1,
              value: getText('payApply.basisConfirmation.accordionTitle'),
              items: [
                {
                  key: 'extendedBasisConfirmationForm',
                  value: extendedBasisConfirmationForm(),
                },
              ],
            },
          ]}
        />
      </div>
    )
  }

  return (
    <div style={{ ...props.style }}>
      <div style={{ width: '30%', display: 'inline-block' }}>
        <label
          className={midas.formLabel}
          style={{
            marginRight: '10px',
            fontWeight: 800,
            verticalAlign: 'middle',
            lineHeight: 2.3,
          }}
        >
          {getText('payApply.basisConfirmation.title')}:
        </label>
      </div>
      <div
        style={{
          width: '70%',
          display: 'inline-flex',
          justifyContent: 'flex-end',
        }}
      >
        <input
          style={{
            padding: '5px',
            marginRight: '10px',
            width: '50%',
          }}
          id="phoneNumber"
          type="text"
          value={phoneNumber || ''}
          onChange={e => handlePhoneNumberChange(e)}
          disabled={isAccountProtected(_getCardOrAccountNumber())}
        />
        <div style={{ display: 'inline-block', marginRight: '10px' }}>
          <Button
            onClick={() => onSend()}
            text={getText('payApply.basisConfirmation.sendSms')}
            disabled={sendSmsButtonDisabled()}
          />
        </div>
        <Button
          onClick={e => handlePrint(e)}
          text={getText('payApply.basisConfirmation.print')}
        />
      </div>
    </div>
  )
}

BasisConfirmationForm.propTypes = {
  card: PropTypes.object.isRequired,
  country: PropTypes.string.isRequired,
  sendBasisConfirmation: PropTypes.func.isRequired,
  printBasisConfirmation: PropTypes.func.isRequired,
  addressInfo: PropTypes.object,
  governmentId: PropTypes.string,
  application: PropTypes.object.isRequired,
  customerType: PropTypes.string.isRequired,
  style: PropTypes.object,
}

const mapStateToProps = state => {
  return {
    applicationId: state.payApply.current.id,
    country: state.storeInfo.currentRepresentative.country,
    addressInfo: state.payApply.addressInfo,
    governmentId: state.payApply.current.governmentId,
    customerType: state.payApply.wizard.wizard,
    application: state.payApply.application,
  }
}

const mapDispatchToProps = dispatch => ({
  sendBasisConfirmation: body => {
    dispatch(sendBasisConfirmation(body))
  },
  getApplication: applicationId => dispatch(getApplication(applicationId)),
})

BasisConfirmationForm.defaultProps = {
  addressInfo: null,
  governmentId: '',
  style: {},
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BasisConfirmationForm)
